// Wave 20 — Public Drop Link page.
//
// Renders at /shared/:token, no auth required (RaphaGuard's public
// allowlist covers /shared/*). Shows the activity summary, top peak
// intervals, and a lightweight elevation profile. Bottom of the page
// has a "Track your own peaks — sign up free" CTA, which is the whole
// point of Drop Links from a viral-loop standpoint.

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import PageLayout from './components/PageLayout';
import ExpirationCountdown from './components/ExpirationCountdown';
import { colors } from './theme';
import SharedCelebration from './components/SharedCelebration';
import DropInteractions from './components/DropInteractions';
import FollowButton from './components/FollowButton';
import {
  fmtDistance,
  fmtPeakDistance,
  fmtElevation,
  fmtPace,
  fmtSpeed,
  useUnitSystem,
  type UnitSystem,
} from './services/useUnitSystem';

interface Interval {
  kind: string;
  value: number;
  unit: string;
  startSec: number;
  endSec: number;
  // Wave 20.49ac — carried from the backend so peak rows can
  // show the effort's distance alongside its value.
  distanceM: number | null;
}

interface ActivityPayload {
  activity: {
    id: string;
    type: 'outdoor_cycling' | 'outdoor_running' | 'indoor_aero';
    startedAt: string;
    endedAt: string | null;
    distanceM: number | null;
    elevationGainM: number | null;
    elevationLossM: number | null;
    maxSpeedMps: number | null;
    avgSpeedMps: number | null;
    elevationProfile: Array<[number, number]>;
  };
  intervals: Interval[];
  rider: { id: number; username: string | null; displayName: string };
  expiresAt: string | null;
  // Wave 20.49am — echoed by backend; powers the "Drop again"
  // affordance on an owner's expired session drop.
  isOwner: boolean;
}

const API = process.env.REACT_APP_API || 'https://api.aero.chat';

function formatDuration(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function formatIntervalValue(
  iv: Interval,
  u: UnitSystem,
  isRun: boolean,
): string {
  switch (iv.unit) {
    case 'W':
      return `${iv.value} W`;
    case 'mps':
      return fmtSpeed(iv.value, u);
    case 'sec_per_m': {
      const mps = iv.value > 0 ? 1 / iv.value : 0;
      // Wave 20.49ac — cycling renders sec/m as mph|kph, running
      // as min/mi|min/km. Matches the in-app peak formatting
      // rules the rider set for cycling vs running.
      return isRun ? fmtPace(mps, u) : fmtSpeed(mps, u);
    }
    case 'sec':
      return formatDuration(iv.value);
    case 'm':
      return fmtDistance(iv.value, u);
    case 'm_per_h':
      return u === 'imperial'
        ? `${Math.round(iv.value * 3.28084).toLocaleString()} ft/h`
        : `${Math.round(iv.value).toLocaleString()} m/h`;
    default:
      return `${iv.value}`;
  }
}

// Human-friendly kind labels — kind strings like "peak_power_5s" get
// turned into "Best 5s Power" so the rider (and their Drop Link
// recipients) don't have to read snake_case.
const PRETTY_TARGETS: Record<string, string> = {
  '5s': '5s', '30s': '30s',
  '1min': '1min', '5min': '5min', '20min': '20min', '60min': '60min',
  '1mi': '1mi', '5k': '5K', '10k': '10K', '40k': '40K',
  'half': 'Half Marathon', 'full': 'Marathon',
};
function kindLabel(kind: string, isRun: boolean = true): string {
  if (kind === 'peak_whole') return 'Entire session';
  if (kind === 'peak_pace') return 'Peak Pace';
  if (kind === 'peak_speed') return 'Peak Speed';
  if (kind === 'peak_power') return 'Peak Power';
  // Wave 20.49ag — cycling shows Grade-Adj SPEED.
  if (kind === 'peak_gap') return isRun ? 'Peak Grade-Adj Pace' : 'Peak Grade-Adj Speed';
  if (kind === 'peak_vam') return 'Peak Climb (VAM)';
  if (kind === 'peak_grade_adj_speed') return 'Peak Speed';
  if (kind === 'peak_grade_adj_pace') return 'Peak Pace';
  const parts = kind.split('_');
  const t = (p: string | undefined): string =>
    p ? (PRETTY_TARGETS[p] ?? p) : '';
  if (parts[0] === 'peak' && parts[1] === 'power' && parts[2]) {
    return `Best ${t(parts[2])} Power`;
  }
  if (parts[0] === 'peak' && parts[1] === 'speed' && parts[2]) {
    return `Fastest ${t(parts[2])}`;
  }
  if (parts[0] === 'peak' && parts[1] === 'pace' && parts[2]) {
    return `Fastest ${t(parts[2])}`;
  }
  if (parts[0] === 'peak' && parts[1] === 'gap') {
    return `Best ${t(parts[2])} Grade-Adj Pace`;
  }
  if (kind === 'effort_zone4_max_sec') {
    return 'Longest Zone 4+ Effort';
  }
  return kind;
}

function activityLabel(type: string): string {
  switch (type) {
    case 'outdoor_cycling':
      return 'Ride';
    case 'outdoor_running':
      return 'Run';
    case 'indoor_aero':
      return 'Indoor Session';
    default:
      return 'Session';
  }
}

export default function SharedActivity() {
  const { token } = useParams<{ token: string }>();
  const units = useUnitSystem();
  const [data, setData] = useState<ActivityPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    // Wave 20.11 — send our JWT if we have one so the server can
    // resolve follower-only drops for authed viewers who follow the
    // sender.
    const jwt = localStorage.getItem(process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token');
    const headers = jwt ? { Authorization: `Bearer ${jwt}` } : undefined;
    fetch(`${API}/public/shared/${encodeURIComponent(token)}`, { headers })
      .then(async (r) => {
        if (r.status === 401) throw new Error('signin_required');
        if (r.status === 403) throw new Error('not_a_follower');
        if (r.status === 404) throw new Error('expired');
        if (!r.ok) throw new Error('unknown');
        return (await r.json()) as ActivityPayload;
      })
      .then((d) => {
        if (!cancelled) {
          setData(d);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e.message || 'unknown');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (loading) {
    return (
      <PageLayout maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
          <CircularProgress sx={{ color: colors.brand }} />
        </Box>
      </PageLayout>
    );
  }

  if (error || !data) {
    const isSignin = error === 'signin_required';
    const isFollow = error === 'not_a_follower';
    return (
      <PageLayout maxWidth="sm">
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            {isSignin
              ? 'Sign in to view this drop'
              : isFollow
              ? 'Friends only'
              : error === 'expired'
              ? 'This Drop Link has expired'
              : 'Drop Link not found'}
          </Typography>
          <Typography sx={{ color: colors.textMuted, mb: 4 }}>
            {isSignin
              ? 'The sender restricted this drop to their followers. Sign in to continue.'
              : isFollow
              ? "You're signed in, but only the sender's friends (followers) can view this drop."
              : error === 'expired'
              ? 'The sender chose an expiration window that has passed.'
              : "The link may be mistyped, or it was never valid."}
          </Typography>
          <Button
            variant="contained"
            href={isSignin ? `/login?next=/shared/${token}` : '/'}
            sx={{ backgroundColor: colors.brand, color: colors.textInverse }}
          >
            {isSignin ? 'Sign in' : 'Go to aero.chat'}
          </Button>
        </Box>
      </PageLayout>
    );
  }

  const startedAt = new Date(data.activity.startedAt);
  const endedAt = data.activity.endedAt
    ? new Date(data.activity.endedAt)
    : null;
  const durationSec = endedAt
    ? Math.round((endedAt.getTime() - startedAt.getTime()) / 1000)
    : 0;
  const topIntervals = data.intervals.slice(0, 8);

  return (
    <PageLayout maxWidth="md">
      <SharedCelebration />
      <Stack spacing={3}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
          <Box>
            <Typography variant="overline" sx={{ color: colors.brand }}>
              {data.rider.displayName} · {activityLabel(data.activity.type)}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5 }}>
              {startedAt.toLocaleDateString()} · {formatDuration(durationSec)}
            </Typography>
            {data.expiresAt &&
              (data.isOwner && new Date(data.expiresAt).getTime() < Date.now() ? (
                // Wave 20.49am — Expired + you own it. Cheers +
                // comments are preserved (backend still returns
                // this drop to you); mint a fresh drop with
                // "Drop again" to keep sharing.
                <Stack spacing={1} sx={{ mt: 0.5 }}>
                  <Typography sx={{ color: colors.textMuted, fontSize: '0.875rem' }}>
                    This Drop Link expired — only you can see it now. The cheers
                    and comments below stay saved.
                  </Typography>
                  <RedropSessionButton sessionId={data.activity.id} />
                </Stack>
              ) : (
                <Typography sx={{ color: colors.textMuted, mt: 0.5, fontSize: '0.875rem' }}>
                  Drop Link expires in{' '}
                  <Box component="span" sx={{ fontFamily: '"JetBrains Mono", monospace', color: colors.brand, fontWeight: 700 }}>
                    <ExpirationCountdown expiresAt={data.expiresAt} />
                  </Box>
                </Typography>
              ))}
          </Box>
          <FollowButton userId={data.rider.id} />
        </Box>

        {/* Aggregate strip */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
            gap: 1.5,
          }}
        >
          <AggregateTile label="Distance" value={fmtDistance(data.activity.distanceM, units)} />
          <AggregateTile label="Elevation" value={fmtElevation(data.activity.elevationGainM, units)} />
          <AggregateTile label="Avg speed" value={fmtSpeed(data.activity.avgSpeedMps, units)} />
          <AggregateTile label="Max speed" value={fmtSpeed(data.activity.maxSpeedMps, units)} />
        </Box>

        {/* Peak intervals */}
        {topIntervals.length > 0 && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
              Peak intervals
            </Typography>
            <Stack spacing={1}>
              {topIntervals.map((iv) => {
                const isRun = data.activity.type === 'outdoor_running';
                const durSec = Math.max(0, iv.endSec - iv.startSec);
                // Wave 20.49ac — Show the peak's distance +
                // duration meta so a session drop-link reader
                // can see the shape of each effort, not just
                // the headline number. Distance only renders
                // when the backend has it (indoor/aero rows
                // will be null and the meta shortens).
                const metaParts: string[] = [];
                if (iv.distanceM != null && iv.distanceM > 0) {
                  metaParts.push(fmtPeakDistance(iv.distanceM, units));
                }
                if (durSec > 0) {
                  metaParts.push(formatDuration(durSec));
                }
                return (
                  <Box
                    key={`${iv.kind}:${iv.startSec}:${iv.endSec}`}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: colors.surface,
                      border: `1px solid ${colors.border}`,
                      gap: 2,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ color: colors.textMuted }}>
                        {kindLabel(iv.kind, isRun)}
                      </Typography>
                      {metaParts.length > 0 && (
                        <Typography sx={{ color: colors.textFaint, fontSize: '0.8rem' }}>
                          {metaParts.join(' · ')}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      label={formatIntervalValue(iv, units, isRun)}
                      sx={{
                        backgroundColor: `${colors.brand}22`,
                        color: colors.brand,
                        fontWeight: 700,
                        fontSize: '1rem',
                        px: 1,
                      }}
                    />
                  </Box>
                );
              })}
            </Stack>
          </Box>
        )}

        {/* Elevation profile (super lightweight SVG) */}
        {data.activity.elevationProfile.length > 3 && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
              Elevation profile
            </Typography>
            <ElevationProfile points={data.activity.elevationProfile} />
          </Box>
        )}

        {/* Wave 20.10 — Reactions + comments */}
        <DropInteractions
          dropType="activity"
          token={token!}
          expiresAt={data.expiresAt}
        />

        {/* Viral CTA — the whole reason Drop Links exist */}
        <Box
          sx={{
            mt: 4,
            p: 3,
            borderRadius: 3,
            backgroundColor: colors.surfaceElevated,
            border: `1px solid ${colors.border}`,
            textAlign: 'center',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Track your own peaks
          </Typography>
          <Typography sx={{ color: colors.textMuted, mb: 2 }}>
            aero.chat records your rides + runs, detects your peak efforts, and lets you drop links like this one.
          </Typography>
          <Button
            variant="contained"
            href="/register"
            sx={{ backgroundColor: colors.brand, color: colors.textInverse, fontWeight: 700 }}
          >
            Sign up free
          </Button>
        </Box>
      </Stack>
    </PageLayout>
  );
}

// Wave 20.49am — Re-mint an expired session drop. POSTs to the
// existing /activities/:id/share endpoint (same one the "Drop
// Link" button in the app uses) with a default 24h window;
// navigates to the new token on success. Cheers/comments on the
// current expired drop stay preserved server-side.
function RedropSessionButton({ sessionId }: { sessionId: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const onClick = async () => {
    setError(null);
    setBusy(true);
    try {
      const jwt = localStorage.getItem(process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token');
      if (!jwt) {
        setError('Sign in to drop again.');
        setBusy(false);
        return;
      }
      const res = await fetch(`${API}/activities/${sessionId}/share`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ window: '24h', visibility: 'public' }),
      });
      if (!res.ok) throw new Error(String(res.status));
      const body = (await res.json()) as { token: string };
      window.location.href = `/dropped/${body.token}`;
    } catch (e: any) {
      setError(e?.message ? `Couldn't drop again (${e.message})` : "Couldn't drop again");
      setBusy(false);
    }
  };
  return (
    <Box>
      <Button
        onClick={onClick}
        disabled={busy}
        variant="contained"
        size="small"
        sx={{
          backgroundColor: colors.brand,
          color: colors.textInverse,
          fontWeight: 800,
          textTransform: 'none',
          alignSelf: 'flex-start',
        }}
      >
        {busy ? 'Dropping…' : 'Drop again →'}
      </Button>
      {error && (
        <Typography sx={{ color: colors.danger, fontSize: '0.75rem', mt: 0.5 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}

function AggregateTile({ label, value }: { label: string; value: string }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
      }}
    >
      <Typography sx={{ fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: colors.textMuted, mb: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '1.25rem', fontWeight: 700, color: colors.text }}>
        {value}
      </Typography>
    </Box>
  );
}

// Simple SVG line chart of elevation over time. Kept dependency-free
// (no chart lib) so the shared page is fast for cold-cache visitors.
function ElevationProfile({ points }: { points: Array<[number, number]> }) {
  const units = useUnitSystem();
  const W = 800;
  const H = 140;
  const PAD = 20;
  const xs = points.map((p) => p[0]);
  const ys = points.map((p) => p[1]);
  const xMin = Math.min(...xs);
  const xMax = Math.max(...xs);
  const yMin = Math.min(...ys);
  const yMax = Math.max(...ys);
  const xRange = Math.max(1, xMax - xMin);
  const yRange = Math.max(1, yMax - yMin);
  const path = points
    .map(([x, y], i) => {
      const px = PAD + ((x - xMin) / xRange) * (W - 2 * PAD);
      const py = H - PAD - ((y - yMin) / yRange) * (H - 2 * PAD);
      return `${i === 0 ? 'M' : 'L'} ${px.toFixed(1)} ${py.toFixed(1)}`;
    })
    .join(' ');
  const areaPath = `${path} L ${W - PAD} ${H - PAD} L ${PAD} ${H - PAD} Z`;
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
      }}
    >
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="140" preserveAspectRatio="none">
        <path d={areaPath} fill={`${colors.brand}22`} />
        <path d={path} fill="none" stroke={colors.brand} strokeWidth={2} />
      </svg>
      <Typography sx={{ mt: 0.5, fontSize: '0.75rem', color: colors.textMuted, textAlign: 'right' }}>
        {fmtElevation(yMin, units)} → {fmtElevation(yMax, units)}
      </Typography>
    </Box>
  );
}
